import { useState, useCallback } from 'react';
import { Upload, AlertCircle, Check } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';

// Worker needed for pdf.js
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.mjs`;

interface CapabilityUploadProps {
    onAnalysisComplete: (data: { capability: string, naics: string[], pastPerformance: string[] }) => void;
}

export default function CapabilityUpload({ onAnalysisComplete }: CapabilityUploadProps) {
    const [isDragging, setIsDragging] = useState(false);
    const [status, setStatus] = useState<'idle' | 'parsing' | 'success' | 'error'>('idle');
    const [message, setMessage] = useState('');

    const handleDrag = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setIsDragging(true);
        } else if (e.type === 'dragleave') {
            setIsDragging(false);
        }
    }, []);

    const extractText = async (file: File): Promise<string> => {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        let fullText = '';

        for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const textContent = await page.getTextContent();
            const pageText = textContent.items.map((item: any) => item.str).join(' ');
            fullText += pageText + '\n';
        }
        return fullText;
    };

    // Client-side regex extraction (Fallback if AI unavailable)
    const simpleExtract = (text: string) => {
        // Find NAICS (6 digit numbers)
        const naicsRegex = /\b\d{6}\b/g;
        const naicsMatches = [...new Set(text.match(naicsRegex) || [])];

        // Basic clean up


        // Return structured data
        return {
            capability: text.substring(0, 1500) + (text.length > 1500 ? '...' : ''), // Limit for now
            naics: naicsMatches,
            pastPerformance: ["Auto-extracted: Please verify from document."] // Placeholder for complex extraction
        };
    };

    const processFile = async (file: File) => {
        if (file.type !== 'application/pdf') {
            setStatus('error');
            setMessage('Only PDF files are supported.');
            return;
        }

        setStatus('parsing');
        try {
            const text = await extractText(file);
            console.log("Extracted Text Length:", text.length);

            // In a real flow, we would send 'text' to Vertex AI here.
            // For now, we use simple regex to prove the "One Click" concept.
            const extractedData = simpleExtract(text);

            onAnalysisComplete(extractedData);
            setStatus('success');
            setMessage('Capabilities extracted successfully!');
        } catch (error) {
            console.error("PDF Parse Error:", error);
            setStatus('error');
            setMessage('Failed to read PDF. Please try again.');
        } finally {
            setIsDragging(false);
        }
    };

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            processFile(e.dataTransfer.files[0]);
        }
    }, []);

    return (
        <div
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={`relative border-2 border-dashed rounded-lg p-8 transition-colors text-center cursor-pointer ${isDragging ? 'border-blue-500 bg-blue-500/10' : 'border-white/10 hover:border-white/20 bg-secondary/30'
                }`}
        >
            <div className="flex flex-col items-center gap-3">
                {status === 'success' ? (
                    <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center text-green-400">
                        <Check className="w-6 h-6" />
                    </div>
                ) : status === 'error' ? (
                    <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center text-red-400">
                        <AlertCircle className="w-6 h-6" />
                    </div>
                ) : (
                    <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400">
                        <Upload className="w-6 h-6" />
                    </div>
                )}

                <div>
                    <h3 className="text-sm font-bold text-gray-200">
                        {status === 'parsing' ? 'Analyzing Document...' : 'Upload Capability Statement'}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                        Drag & Drop or <span className="text-blue-400">Click to Browse</span>
                    </p>
                    <p className="text-[10px] text-gray-600 mt-2">
                        Supported: PDF (Max 10MB). We detect NAICS and Past Performance.
                    </p>
                </div>

                {message && (
                    <p className={`text-xs font-medium ${status === 'error' ? 'text-red-400' : 'text-green-400'}`}>
                        {message}
                    </p>
                )}

                <input
                    type="file"
                    className="hidden"
                    accept=".pdf"
                    onChange={(e) => e.target.files?.[0] && processFile(e.target.files[0])}
                    id="pdf-upload"
                />
                <label
                    htmlFor="pdf-upload"
                    className="absolute inset-0 cursor-pointer"
                />
            </div>
        </div>
    );
}
